import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Justify, PagingQuery, DatePicker } from 'tea-component';
const { QuarterPicker, MonthPicker } = DatePicker;
import Filter from '@/pages/intelligence/pages/TopCharts/components/CommonFilter';
import SearchRecent from '@/pages/intelligence/components/SearchRecent';
import DemographicsTable from '@/pages/intelligence/pages/TopCharts/MobileGames/components/Tables/DemographicsTable';
import Visualis, {
  SEGMENT_KEYS
} from '@/pages/intelligence/pages/TopCharts/MobileGames/components/Visualis/DemographicsVisualis';
import { getGameBasicinfoNew } from '@/pages/intelligence/services/topcharts';
import {
  ageBreakdownList,
  getAgeSortList,
  genderBreakdownList,
  handleTableBarList,
  companyFilterParams,
} from './_config/utils';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { TOPGAMENUM } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import {
  TableSelectStatusType,
  TableSelectionType,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { RecordItemType } from '@/pages/intelligence/components/SearchRecent/type';
import { getCountryFlag } from '@/pages/home/_config/countries';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import useDemographicsRegionsOptions from '@/pages/intelligence/hooks/useDemographicsRegionsOptions';
import { getFilterName, getFilterValueStorage } from '@/pages/intelligence/components/Filter/util';
import { useSelector, history, useIntl } from 'umi';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import {
  commonMobileDefaultFilterValue,
  handleDemographicsFilterParamsToApi,
} from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import {
  APPMAGIC_TAG_DEFAULT_VALUES,
  SENSOR_TOWER_TAG_KEYS,
  buildAppMagicTagSpecialParams,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { FilterTimeType, getDbiFilterParams } from '@intelligence/utils/filterUtils';
import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import useMobileGameEngineOptions from '@intelligence/hooks/useMobileGameEngineOptions';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import useSenTowerTag from '@/pages/intelligence/hooks/useSenTowerTag';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import useHotTagsOptions from '@intelligence/hooks/useHotTagsOptions';
import { parseUrlFilterParams } from '@intelligence/v3/utils/filterTagNameAnalyser';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import { GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE } from '@intelligence/components/GenreSelect/constants';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import {
  getTopMobilesDemographicsCustomValidUrlParams,
  topchartMobileDemographicsValidCfg,
  hasShortLinkRatio,
} from '@intelligence/_config/urlPageValidCfg';
import {
  getOriginalShortLinkFilter,
  updateShortLinkPath,
  URL_CHART_METRIC,
  URL_SHORT_LINK_NAME,
  URL_CHART_TYPE,
  URL_SORT_NAME,
  URL_ORDER,
  URL_RATIO_TYPE,
  updateShortLinkFilterId,
  updateTimeFilterValue,
  URL_FILTER_KEY,
} from '@intelligence/utils/urlPathCfg';
import {
  handleTableDefaultSelectGames,
  filterGamesWithRank,
} from '@/pages/intelligence/utils/tableUtils';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';
import { getMobileTopChartsFilterTips } from '@intelligence/pages/TopCharts/MobileGames/_config/mobileTopChartsFilterTips';
import { buildTopChartsTagClickHandler } from '@intelligence/v3/utils/sensorTowerTagColumns';
import useMobileTopChartsTagDetails from '@intelligence/v3/pages/TopCharts/MobileGames/_hooks/useMobileTopChartsTagDetails';

import './index.scss';
import { addGlobalCountryCode, addGlobalRegion } from '@/pages/intelligence/v3/utils/region';
import { SOURCE_LIST } from '../components/GamesDetails/Demographics/_config/constant';
import { generateGenreSelectValueSimple } from '@intelligence/v3/utils/genre';
import { AppMagicGenreTagType } from '@intelligence/components/TableComponents/components/AppMagicGenreTagList';

interface IDemographicsProps {
  /** TopCharts 主入口显式传递；复用入口不传时沿用 common 作用域。 */
  pageId?: string;
  /** 游戏详情页复用时继续透传既有详情参数。 */
  [key: string]: any;
}

export default function Demographics(props: IDemographicsProps) {
  const { pageId } = props;
  const { formatMessage, locale } = useIntl();
  const isCn = locale === LANGUAGE_CN;
  const entityType = 'mobile';
  const pageLogInfo = dbiPageLogMes.topchartMobileGamesDemographics;
  const hasUrlShortLink = hasShortLinkRatio.topMobileDemographics;
  const [shortLinkLoading, setShortLinkLoading] = useState(hasUrlShortLink);
  const [urlCustomParams, setUrlCustomParams] = useState({});
  const [shortLinkValidQuery, setShortLinkValidQuery] = useState({
    [URL_CHART_TYPE]: 'bar',
    [URL_CHART_METRIC]: 'gender',
  });
  const [shortLinkFilterValue, setShortLinkFilterValue] = useState(null);

  // 当前数据源（控制时间筛选器形态）
  const [activeDatasource, setActiveDatasource] = useState(SOURCE_LIST.SENSORTOWER);

  // 国家下拉 options：切换 datasource / granularity 时要立即刷新（榜单页：app_ids 永远 []）
  const [countryFilterParams, setCountryFilterParams] = useState({
    source: SOURCE_LIST.SENSORTOWER,
    granularity: SOURCE_LIST.LIFETIME,
  });

  const {
    marketLoading: filterLoading,
    regionOptions,
    allMarkets,
  } = useDemographicsRegionsOptions({
    appIds: [],
    source: countryFilterParams?.source,
    granularity: countryFilterParams?.granularity,
    waitForParams: true,
  });

  const regionOptionsForFilter = useMemo(() => {
    if (activeDatasource === SOURCE_LIST.SENSORTOWER) {
      return addGlobalRegion(regionOptions);
    }
    // data.ai 不展示 global
    if (!Array.isArray(regionOptions)) {
      return regionOptions;
    }
    return regionOptions.filter((item: any) => item?.area_code !== 'global');
  }, [activeDatasource, regionOptions]);

  const allMarketsForFilter = useMemo(() => {
    if (activeDatasource === SOURCE_LIST.SENSORTOWER) {
      return addGlobalCountryCode(allMarkets);
    }
    if (!Array.isArray(allMarkets)) {
      return allMarkets;
    }
    return allMarkets.filter((item: any) => item !== 'global');
  }, [activeDatasource, allMarkets]);

  const { tagLoading, tagConfig } = useSenTowerTag({});

  // 品类 options（支持 App Magic 四级联动）
  const { loadingStd: definitionsLoading, genreMap, genreOptionsMap, filterTags } =
    useQueryDefinition();
  const { mobileGameEngineOptions } = useMobileGameEngineOptions();

  // 热门标签
  const { hotTagsOptions, filterTagName, hotTagsLoading } = useHotTagsOptions('mobile');

  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.mobileDemographics?.type,
    rangeTimeType: FilterTimeType.sameMonth,
  });

  const appMagicTagConfig = useMemo(
    () => buildAppMagicTagSpecialParams(filterTags, isCn),
    [isCn, filterTags],
  );

  const initPage = async () => {
    setShortLinkLoading(true);
    // 在url上取token，有则请求接口， 判断异常，返回函数值和url上的其他配置参数
    const { [URL_FILTER_KEY]: urlFilter } = await getOriginalShortLinkFilter();
    if (urlFilter) {
      // 有的筛选器的token时才用后面的参数，没有则不用
      const { params, validQuery } = getTopMobilesDemographicsCustomValidUrlParams(
        history,
        topchartMobileDemographicsValidCfg,
      );
      setUrlCustomParams({ ...params });
      setShortLinkValidQuery({ ...shortLinkValidQuery, ...validQuery });
      setShortLinkFilterValue(urlFilter);
    }
    setShortLinkLoading(false);
  };

  useEffect(() => {
    buriedPointPage(pageLogInfo);
    // 进入页面时判断是否有tokenQuery等，有则进入
    initPage();
  }, []);

  const defaultFilterValue = {
    ...commonMobileDefaultFilterValue,
    ...APPMAGIC_TAG_DEFAULT_VALUES,
    [filterKeyMap.GENRETOR]: {
      ...commonMobileDefaultFilterValue?.[filterKeyMap.GENRETOR],
      source: 'sensortower',
    },
    // 默认：SensorTower（单选），避免默认透传成 ["sensortower","appannie"]
    [filterKeyMap.DATASOURCE]: {
      value: [SOURCE_LIST.SENSORTOWER],
    },
    // 默认：Lifetime（SensorTower 下拉默认选中 Lifetime）
    [filterKeyMap.RANGE_TIME]: {
      type: 'lifetime',
      value: ['', ''],
    },
    // 默认：国家选 global（因为默认 datasource 是 SensorTower）
    [filterKeyMap.COMPOSED_COUNTRY_SELECT]: {
      value: {
        type: 'single',
        countries: ['global'],
      },
    },
  };

  const changeSourceValue = useMemo(
    () => {
      return {
        [filterKeyMap.RANGE_TIME]: {
          // data.ai 维持 Month-range；Sensor Tower 用 Lifetime/Quarter 下拉（默认 Lifetime）
          type: activeDatasource === SOURCE_LIST.APPANNIE ? 'month' : 'lifetime',
          value: ['', ''],
        },
      }
    },
    [activeDatasource],
  );

  const groupedFilter = filterKeyMap.defineFilterGroups(
    [
      {
        key: 'popular',
        titleKey: 'dbi_filter_group_popular',
        filterKeys: [
          filterKeyMap.RANGE_TIME,
          filterKeyMap.COMPOSED_COUNTRY_SELECT,
          filterKeyMap.PLATFORM,
          filterKeyMap.GENRETOR,
          filterKeyMap.HOT_TAGS_SOURCE,
          filterKeyMap.ALL_Games,
          filterKeyMap.DATASOURCE,
          filterKeyMap.ALL_COMPANYS,
          filterKeyMap.RELEASE_TIME,
        ],
      },
      {
        key: 'game_performance',
        titleKey: 'dbi_filter_group_game_performance',
        filterKeys: [],
      },
      {
        key: 'sensor_tower',
        titleKey: 'dbi_filter_group_sensor_tower',
        iconCode: 'sensortower',
        filterKeys: SENSOR_TOWER_TAG_KEYS,
      },
      {
        key: 'app_magic',
        titleKey: 'dbi_filter_group_app_magic',
        iconCode: 'appmagic',
        filterKeys: filterKeyMap.APPMAGIC_TAG_KEYS,
      },
      {
        key: 'igdb',
        titleKey: 'dbi_filter_group_igdb',
        iconCode: 'igdb',
        filterKeys: [filterKeyMap.GAME_ENGINE],
      },
    ],
    { ungroupedKeys: [filterKeyMap.MY_FILTER] },
  );

  const filterCfg: TopchartFilterCfgType = {
    ...groupedFilter,
    companyFilterParams,
    defaultValue: defaultFilterValue,
    resetFilterValue: {
      ...defaultFilterValue,
      ...changeSourceValue,
      // [filterKeyMap.RANGE_TIME]: {
      //   type: 'month',
      //   value: [moment().subtract(1, 'months'), moment().subtract(1, 'months')],
      // },
    },
    specialParams: {
      myFilterStyle: { width: 140 },
      regionClassName: 'hot-tags-region-select',
      genreClassName: 'hot-tags-genre-select',
      filterTips: getMobileTopChartsFilterTips(formatMessage),
      regionOptions: regionOptionsForFilter, // 国家筛选器options
      defalutMarket: activeDatasource === SOURCE_LIST.SENSORTOWER ? ['global'] : ['us'],
      disableGlobal: activeDatasource !== SOURCE_LIST.SENSORTOWER,
      disableNoChina: true,
      // rangeTimeType: FilterTimeType.sameMonth,

      // 品类筛选器：启用 4 源 + App Magic 四级（4列），并输出标准化品类参数
      isStdGenreValuesHandler: true,
      genreOverlayClassName: 'cascade-mode',
      genreOptionsLoading: definitionsLoading,
      genreOptions: genreMap,
      genreSources: GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE,
      disableIegg: false,
      disableAnnie: false,
      disableSensorTower: false,

      sensorTowerTagConfig: tagConfig,
      ...appMagicTagConfig,
      gameEngineOptions: mobileGameEngineOptions,
      isSimpleCountry: true,
      [filterKeyMap.ALL_Games]: {
        params: { entity_type: entityType, game_type: 'current_game' },
      },
      sourceFilterSingleType: 'single',
      [filterKeyMap.DATASOURCE]: {
        isShowButtonLabel: true, // 显示标签
      },
      // 时间筛选器：data.ai 维持 Month-range（无下拉 + 可输入）；Sensor Tower 用默认样式 + Lifetime/Quarter 下拉
      rangeTimeType:
        activeDatasource === SOURCE_LIST.SENSORTOWER ? FilterTimeType.commonRange : FilterTimeType.rangeMonth,
      showSelectType: activeDatasource !== SOURCE_LIST.APPANNIE,
      showShortcut: false,
      typeList: activeDatasource === SOURCE_LIST.SENSORTOWER ? ['lifetime', 'quarter'] : ['month'],
      [filterKeyMap.RANGE_TIME]: {
        // 注意：RANGE_TIME 自身也需要显式声明 showSelectType/typeList，避免切源后沿用旧 inputList 配置
        showSelectType: activeDatasource !== SOURCE_LIST.APPANNIE,
        typeList: activeDatasource === SOURCE_LIST.SENSORTOWER ? ['lifetime', 'quarter'] : ['month'] ,

        // Sensor Tower：Lifetime 禁用输入（固定范围展示）；Quarter 允许选择且只选一个季度
        selectClassName: 'dbi_demographics-select',
        customComponentClassName: 'dbi_demographics-custompicker',
        rangePickerClassName: 'dbi_demographics-rangepicker',
        overlayClassName: 'dbi_demographics-rangepicker-overlay',
        optionsMap: {
          lifetime: { value: 'lifetime', text: 'Lifetime', text_cn: '全周期', type: 'date' },
        },
        customOption: {
          lifetime: {
            disabled: activeDatasource === SOURCE_LIST.SENSORTOWER,
          },
          quarter: {
            showSelectType: false,
            isSingleSelect: true,
            customRender: ({ value, onChange, disabled, className, ...rest }: any) => {
              // 外部 value 为 [Moment, Moment]，QuarterPicker 需要单个 Moment
              const pickerValue = Array.isArray(value) ? (value[0] && value[0] !== '' ? value[0] : undefined) : (value || undefined);
              return (
                <QuarterPicker
                  className={className}
                  value={pickerValue}
                  onChange={(val: any) => {
                    if (!val) return onChange([null, null]);
                    const startOfQuarter = val.clone().startOf('quarter');
                    onChange([startOfQuarter, startOfQuarter.clone()]);
                  }}
                  disabled={disabled}
                  {...rest}
                />
              );
            },
          },
          month: {
            showSelectType: false,
            isSingleSelect: true,
            customRender: ({ value, onChange, disabled, className, ...rest }: any) => {
              const pickerValue = Array.isArray(value) ? (value[0] && value[0] !== '' ? value[0] : undefined) : (value || undefined);
              return (
                <MonthPicker
                  className={className}
                  value={pickerValue}
                  onChange={(val: any) => {
                    if (!val) return onChange([null, null]);
                    const startOfMonth = val.clone().startOf('month');
                    onChange([startOfMonth, startOfMonth.clone()]);
                  }}
                  disabled={disabled}
                  {...rest}
                />
              );
            },
          },
        },
      },
      entityType,
      hotTagsOptions: hotTagsOptions || [],
      filterTagName,
    },
    showConfirmButton: true,
    haveFilterSaveBtn: true,
    handleFilterToApiParams: (params: any) =>
      handleDemographicsFilterParamsToApi(params, entityType),
    hasUrlShortLink,
    enableSticky: true,
  };

  return (
    <DemographicsComp
      {...props}
      pageId={pageId}
      entityType={entityType}
      pageLogInfo={dbiPageLogMes.topchartMobileGamesDemographics}
      filterCfg={filterCfg}
      filterLoading={filterLoading || tagLoading || shortLinkLoading || definitionsLoading || (!!filterTagName && hotTagsLoading)}
      allMarkets={allMarketsForFilter}
      rangeTimeComponentDefault={latestTimeTypeHasData}
      urlCustomParams={urlCustomParams}
      shortLinkValidQuery={shortLinkValidQuery}
      shortLinkFilterValue={shortLinkFilterValue}
      setActiveDatasource={setActiveDatasource}
      setCountryFilterParams={setCountryFilterParams}
      genreOptionsMap={genreOptionsMap}
      hotTagsOptions={hotTagsOptions}
      filterTagName={filterTagName}
    />
  );
}

function DemographicsComp(props: any) {
  const {
    filterLoading: marketLoading,
    allMarkets,
    rangeTimeComponentDefault,
    filterCfg,
    pageLogInfo,
    shortLinkValidQuery,
    shortLinkFilterValue,
    urlCustomParams,
    entityType,
    setActiveDatasource,
    setCountryFilterParams,
    genreOptionsMap,
    hotTagsOptions,
    filterTagName,
    pageId,
  } = props;

  const DEFAULT_FORM_VALUE = filterCfg?.defaultValue;
  // Demographics 的时间可能是 lifetime/quarter，必须只从当前 tab 分区读取。
  const { filterExtend, setFilterExtend, isOperate } = useFilterExtend({ pageId });
  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });
  const [segVal, setSegVal] = useState(shortLinkValidQuery?.[URL_CHART_METRIC] || 'gender');
  const [filterValue, setFilterVale] = useState(isOperate ? filterExtend : DEFAULT_FORM_VALUE);

  const { locale } = useIntl();

  // 当前选中的 hotTag option，用于下载文件名前缀
  const [selectedHotTagOption, setSelectedHotTagOption] = useState<any>(null);

  // hotTagsOptions 加载完成后，匹配 filterTagName 并设置默认选中
  useEffect(() => {
    if (!filterTagName || !hotTagsOptions?.length) return;
    const matchedOption = hotTagsOptions.find((opt: any) => opt?.name === filterTagName);
    if (!matchedOption?.value) return;
    setSelectedHotTagOption(matchedOption);
  }, [filterTagName, hotTagsOptions]);

  // 使用 ref 保存最新的 genreOptionsMap，避免 useCallback 闭包陈旧引用
  const genreOptionsMapRef = React.useRef(genreOptionsMap);
  genreOptionsMapRef.current = genreOptionsMap;

  // HotTagsSource 选中回调：只更新页面筛选器值，不立即请求
  const handleHotTagConfirm = React.useCallback(async (option: any) => {
    if (!option?.value) return;
    setSelectedHotTagOption(option);
    try {
      const parsedValue = await parseUrlFilterParams(option.value, { genreOptionsMap: genreOptionsMapRef.current });
      if (parsedValue && Object.keys(parsedValue).length > 0) {
        const nextFilterValue = {
          ...DEFAULT_FORM_VALUE,
          ...parsedValue,
          [filterKeyMap.HOT_TAGS_SOURCE]: { value: option },
        };
        setFilterVale(nextFilterValue);
        filterRef.current?.setFormValue(nextFilterValue);
      }
    } catch (err) {
      console.error('[DemographicsComp] handleHotTagConfirm failed:', err);
    }
  }, [DEFAULT_FORM_VALUE]);

  // 将 onHotTagConfirm 注入 filterCfg.specialParams
  const filterCfgWithHotTag = React.useMemo(() => ({
    ...filterCfg,
    specialParams: {
      ...filterCfg?.specialParams,
      onHotTagConfirm: handleHotTagConfirm,
    },
  }), [filterCfg, handleHotTagConfirm]);

  // 避免 datasource 切换导致 marketLoading 变化后重复触发"初始化请求"
  const initFetchKeyRef = useRef('');
  // 我们在 getGameData 内部主动更新短链/回填筛选器时，会引起 shortLinkFilterValue 变化；此时不应再触发初始化请求兜底
  const skipNextInitFetchRef = useRef(false);
  // 用户刚点击过"确认"时，跳过初始化 useEffect 的兜底请求，避免重复请求/参数被覆盖
  const lastUserConfirmAtRef = useRef(0);

  // datasource 切到 data.ai 时：等国家 options 回来后再自动发一次请求（market 取下拉第一个）
  const [pendingDatasourceFetch, setPendingDatasourceFetch] = useState<
    | {
        source: string;
        granularity: string;
      }
    | null
  >(null);
  // const [filterUrlParams, setFilterUrlParams] = useState({})
  // 在筛选器这里拿到缓存的值，并渲染
  const filterSaveName = getFilterName(userInfo);
  const filterSaveData = getFilterValueStorage(userInfo);
  // const { marketLoading, regionOptions, allMarkets, } = useDemographicsRegionsOptions({ appIds: [] });

  const APP_ID = 'app_id';
  const [tableLoading, setTableLoading] = useState(true);
  const [isVisualisLoading, setIsVisualisLoading] = useState(true);
  // 宽表勾选的数据
  const [defaultSelectionGames, setDefaultSelectionGames] = useState<TableSelectionType[]>([]);
  const [tableSelection, setTableSelection] = useState<TableSelectionType[]>([]);

  const [tableListForClearSearch, setTableListForClearSearch] = useState<any[]>([]);
  const [totalForClearSearch, setTotalForClearSearch] = useState(0);
  const pageSizeBeforeSearchRef = useRef<number>(50);
  const [barOrPieData, setBarOrPieData] = useState<any[]>([]); // 折线图数据源

  const [operatedGame, setOperatedGame] = useState(false);
  const [ageSortList, setAgeSortList] = useState<any[]>([]);
  // 宽表总数据条数
  const [tableTotal, setTableTotal] = useState(0);
  // 宽表数据
  const [tableData, setTableData] = useState<any[]>([]);
  const { gameDetailsLoading, gameDetailsMapping } = useMobileTopChartsTagDetails(tableData);
  const [sortKeyName, setSortKeyName] = useState<any>('MAU');
  const [fetchParams, setFetchParams] = useState<any>({
    entity_type: 'mobile',
    region_type: 'market',
    markets: ['global'],
    // date: '',
    start_date: '',
    end_date: '',
    source: SOURCE_LIST.SENSORTOWER,
    granularity: SOURCE_LIST.LIFETIME,
    filter: {
      appannie_genre: [],
      appmagic_genre: [],
      iegg_genre: [],
      sensortower_genre: [],
    },
    order: 'desc',
    order_metric: 'mau',
    // order_type: '',
    ratio_type: 0,
    page: 1,
    // page_size: 100,
    page_size: 50,
    company: [],
    start_release_date: null,
    end_release_date: null,
    search_by_name: '',
    genre_source: 'sensortower',
    iegg_genre_req: [],
    game_code: [],
    search_game_code: {},
    platforms: ['appstore', 'googleplay'],
    sensortower_tag_filter: [],
    filter_ids: [],
    rank_ids: [],
  });

  // 记录 lifetime 模式下后端返回的时间，供下载弹窗使用（切到 quarter 后该值不变）
  const lifetimeDateRef = useRef<{ start: string; end: string }>({ start: '', end: '' });
  // 缓存接口返回的各数据源/时间类型的"最后有数据时间"，切换数据源或时间筛选器时回填空时间
  const latestDatesRef = useRef<{
    lifetime_latest_start_date: string;
    lifetime_latest_end_date: string;
    data_ai_latest_start_date: string;
    data_ai_latest_end_date: string;
    quarter_latest_date: string;
  }>({
    lifetime_latest_start_date: '',
    lifetime_latest_end_date: '',
    data_ai_latest_start_date: '',
    data_ai_latest_end_date: '',
    quarter_latest_date: '',
  });
  const filterRef = useRef<any>(null);

  const resolveReqSource = (apiParams: any, filterValues: any) => {
    const rangeTimeType = filterValues?.[filterKeyMap.RANGE_TIME]?.type;
    // Quarter/Lifetime 只属于 SensorTower：强制 source 走 sensortower，避免误判成 appannie 导致 granularity=monthly
    if (rangeTimeType === 'quarter' || rangeTimeType === 'lifetime') {
      return SOURCE_LIST.SENSORTOWER;
    }

    const dsValue = filterValues?.[filterKeyMap.DATASOURCE]?.value;
    const ds = Array.isArray(dsValue) ? dsValue?.[dsValue.length - 1] : dsValue;
    if (typeof ds === 'string' && ds) {
      return ds;
    }
    const source = apiParams?.source;
    const sourceVal = Array.isArray(source) ? source[0] : source;
    if (typeof sourceVal === 'string' && sourceVal) {
      return sourceVal;
    }
    return SOURCE_LIST.SENSORTOWER;
  };

  const resolveReqGranularity = (source: string, filterValues: any) => {
    // data.ai（appannie）没有下拉选择，固定 monthly
    if (source === SOURCE_LIST.APPANNIE) {
      return 'monthly';
    }
    const rangeTimeType = filterValues?.[filterKeyMap.RANGE_TIME]?.type;
    // Quarter -> quarterly；其余（Lifetime）-> lifetime
    if (rangeTimeType === 'quarter') {
      return 'quarterly';
    }
    return SOURCE_LIST.LIFETIME;
  };

  const adjustFormValue = (key: string, formValue: any) => {
    const dsValue = formValue?.[filterKeyMap.DATASOURCE]?.value;
    const ds = Array.isArray(dsValue) ? dsValue?.[dsValue.length - 1] : dsValue;
    // 切换时间类型（如 quarter → lifetime 或 data.ai → lifetime）时，回填已缓存的时间
    if (key === filterKeyMap.RANGE_TIME) {
      const rangeTimeType = formValue?.[filterKeyMap.RANGE_TIME]?.type;

      // 只有时间类型真正改变（quarter↔lifetime）时才刷新国家 options，
      // 同一类型下只切值（如 Q4→Q3）不需要刷新，避免不必要的父组件重渲染导致筛选器值被覆盖
      const newGranularity = resolveReqGranularity(ds, formValue);
      setCountryFilterParams((prev: any) => {
        if (prev?.source === ds && prev?.granularity === newGranularity) return prev;
        return { source: ds, granularity: newGranularity };
      });
      if (rangeTimeType === 'lifetime') {
        // 优先用 lifetimeDateRef（曾请求过 lifetime），兜底用 latestDatesRef（首次请求 quarter 时接口也会返回 lifetime_latest_*）
        const ltStart = lifetimeDateRef.current.start || latestDatesRef.current.lifetime_latest_start_date;
        const ltEnd = lifetimeDateRef.current.end || latestDatesRef.current.lifetime_latest_end_date;
        if (ltStart && ltEnd) {
          return {
            [filterKeyMap.RANGE_TIME]: {
              type: 'lifetime',
              value: [ltStart, ltEnd],
            },
          };
        }
      }
      // quarter：仅在用户时间为空时才用缓存的 quarter_latest_date 回填，不覆盖用户已选的季度
      if (rangeTimeType === 'quarter') {
        const currentValue = formValue?.[filterKeyMap.RANGE_TIME]?.value;
        const hasValue = Array.isArray(currentValue) && currentValue[0] && currentValue[1];
        if (!hasValue && latestDatesRef.current.quarter_latest_date) {
          return {
            [filterKeyMap.RANGE_TIME]: {
              type: 'quarter',
              value: [latestDatesRef.current.quarter_latest_date, latestDatesRef.current.quarter_latest_date],
            },
          };
        }
      }
      return {};
    }

    if (key !== filterKeyMap.DATASOURCE) {
      return {};
    }
    setActiveDatasource?.(ds);
    // 切换数据源时刷新国家 options
    setCountryFilterParams({
      source: ds,
      granularity: resolveReqGranularity(ds, formValue),
    });
    // 切源时同步时间：使用缓存的各数据源"最后有数据时间"回填，避免切过去是空时间
    let timeValue: [string, string] = ['', ''];
    let timeType = 'lifetime';
    if (ds === SOURCE_LIST.APPANNIE) {
      timeType = 'month';
      if (latestDatesRef.current.data_ai_latest_end_date) {
        timeValue = [latestDatesRef.current.data_ai_latest_end_date, latestDatesRef.current.data_ai_latest_end_date];
      }
    } else {
      // SensorTower 默认 lifetime
      if (lifetimeDateRef.current.start && lifetimeDateRef.current.end) {
        timeValue = [lifetimeDateRef.current.start, lifetimeDateRef.current.end];
      } else if (latestDatesRef.current.lifetime_latest_start_date && latestDatesRef.current.lifetime_latest_end_date) {
        timeValue = [latestDatesRef.current.lifetime_latest_start_date, latestDatesRef.current.lifetime_latest_end_date];
      }
    }
    return {
      // datasource 强制单选（CommonFilter 内部有 cascader 形态，避免透传成 ["sensortower","appannie"]）
      [filterKeyMap.DATASOURCE]: {
        ...(formValue?.[filterKeyMap.DATASOURCE] || {}),
        value: formValue.dataSource.value,
      },
      [filterKeyMap.RANGE_TIME]: {
        type: timeType,
        value: timeValue,
      },
      // SensorTower：切源时国家默认 global；data.ai：不要提前写死（等 options 回来后取第一个）
      ...(ds === SOURCE_LIST.SENSORTOWER
        ? {
            [filterKeyMap.COMPOSED_COUNTRY_SELECT]: {
              ...(formValue?.[filterKeyMap.COMPOSED_COUNTRY_SELECT] || {}),
              value: {
                type: 'single',
                countries: ['global'],
              },
            },
          }
        : {}),
    };
  };

  const handleTableDataFormat = (list: any[], params: any) => {
    if (!Array.isArray(list)) {
      return [];
    }
    let firstIconRank = -1; // 展示ICON提示
    const tableList = list?.map((item) => {
      const metricList = item?.metric_list;
      const ageMetricList = handleTableBarList(params.order_metric, ageBreakdownList, metricList);
      const genderMetricList = handleTableBarList(
        params.order_metric,
        genderBreakdownList,
        metricList,
      );
      if (item?.app_ids?.length > 0 && firstIconRank < 0) {
        firstIconRank = item?.rank;
      }
      return {
        ...item,
        ageMetricList,
        genderMetricList,
        firstIconRank,
      };
    });
    return tableList;
  };

  useEffect(() => {
    if (shortLinkValidQuery) {
      if (shortLinkValidQuery[URL_CHART_METRIC] !== undefined) {
        setSegVal(shortLinkValidQuery[URL_CHART_METRIC]);
      }
    }
  }, [shortLinkValidQuery]);

  useEffect(() => {
    if (!marketLoading && filterCfg?.hasUrlShortLink) {
      updateShortLinkPath({
        shortLinkQuery: {
          [URL_SORT_NAME]: fetchParams.order_metric,
          [URL_ORDER]: fetchParams.order,
          [URL_RATIO_TYPE]:
            fetchParams?.order_metric === 'mau' ? fetchParams.ratio_type : fetchParams?.order_type,
          [URL_CHART_METRIC]: segVal,
        },
      });
    }
  }, [
    marketLoading,
    segVal,
    fetchParams.order_metric,
    fetchParams.order,
    fetchParams.ratio_type,
    fetchParams?.order_type,
  ]);

  useEffect(() => {
    if (!userInfo?.userid || marketLoading) {
      // 拿缓存值需要拿到用户id
      return;
    }

    // marketLoading 会因为切源刷新国家 options 而变化；这里用 key 做防抖，避免重复触发"初始化请求"
    // 注意：不把 isOperate 放入 key，因为初始化内 setFilterExtend 会把 isOperate 从 false 翻成 true，导致 key 变化、防重失效、发出重复请求
    const initKey = `${userInfo?.userid || ''}|${shortLinkFilterValue || ''}|${JSON.stringify(
      urlCustomParams || {},
    )}`;
    if (initFetchKeyRef.current === initKey) {
      return;
    }

    // 我们在 getGameData 内部主动更新短链时，shortLinkFilterValue 会变化；这次变化不应触发初始化兜底请求（否则会造成一次确认两次请求）
    if (skipNextInitFetchRef.current) {
      skipNextInitFetchRef.current = false;
      initFetchKeyRef.current = initKey;
      return;
    }

    // 用户刚点过"确认"时，避免初始化兜底请求抢跑/覆盖用户确认请求参数
    if (Date.now() - (lastUserConfirmAtRef.current || 0) < 1500) {
      initFetchKeyRef.current = initKey;
      return;
    }

    initFetchKeyRef.current = initKey;

    setTableLoading(true);
    setIsVisualisLoading(true);

    let newFetchParams = { ...fetchParams };
    const hasUrlShortLinkVal = history.location?.query?.[URL_SHORT_LINK_NAME];
    let { filterValue: newFilterValues, filterParams } = getDbiFilterParams({
      userInfo,
      urlCustomParams,
      urlShortLinkFilter: shortLinkFilterValue,
      filterSaveData: filterSaveData?.apiValue,
      filterExtend,
      specialParams: filterCfg?.specialParams,
      isOperate,
      filterName: filterSaveName,
      filterKeyList: filterCfg?.filterKeyList ?? Object.keys(DEFAULT_FORM_VALUE),
    });

    newFilterValues = {
      ...DEFAULT_FORM_VALUE,
      ...newFilterValues,
    };
    // Demographics 是单选数据源页面：继承值的 DATASOURCE 可能是多选数组 ['sensortower','appannie']，
    // 需要取第一个作为当前数据源；同时继承值的时间类型可能被重置为 date，sensortower 下需要还原为 lifetime
    if (isOperate) {
      const dsValue = newFilterValues?.[filterKeyMap.DATASOURCE]?.value;
      const firstDs = Array.isArray(dsValue) ? dsValue[0] : dsValue;
      newFilterValues = {
        ...newFilterValues,
        [filterKeyMap.DATASOURCE]: {
          ...(newFilterValues?.[filterKeyMap.DATASOURCE] || {}),
          value: firstDs ? [firstDs] : [SOURCE_LIST.SENSORTOWER],
        },
      };
      // 继承时时间类型被重置为 date，但 sensortower 下默认应为 lifetime
      const rangeType = newFilterValues?.[filterKeyMap.RANGE_TIME]?.type;
      if ((!firstDs || firstDs === SOURCE_LIST.SENSORTOWER) && rangeType === 'date') {
        newFilterValues = {
          ...newFilterValues,
          [filterKeyMap.RANGE_TIME]: {
            ...newFilterValues?.[filterKeyMap.RANGE_TIME],
            type: 'lifetime',
            value: ['', ''],
          },
        };
      }
    }

    const apiParams = filterCfg?.handleFilterToApiParams?.(filterParams) || {};
    const reqSource = resolveReqSource(apiParams, newFilterValues);
    const reqGranularity = resolveReqGranularity(reqSource, newFilterValues);

    // data.ai 没有 global 选项：继承值中可能残留 global（如从其他 Tab 切回），需修正为 us
    if (reqSource === SOURCE_LIST.APPANNIE) {
      const currentMarkets = newFilterValues?.[filterKeyMap.COMPOSED_COUNTRY_SELECT]?.value?.countries;
      if (Array.isArray(currentMarkets) && currentMarkets.includes('global')) {
        const fixedCountries = ['us'];
        newFilterValues = {
          ...newFilterValues,
          [filterKeyMap.COMPOSED_COUNTRY_SELECT]: {
            ...(newFilterValues?.[filterKeyMap.COMPOSED_COUNTRY_SELECT] || {}),
            value: {
              ...(newFilterValues?.[filterKeyMap.COMPOSED_COUNTRY_SELECT]?.value || {}),
              countries: fixedCountries,
            },
          },
        };
        if (apiParams?.regions) {
          apiParams.regions = fixedCountries;
        }
      }
    }

    // 如果有短链接，设置为继承模式
    if (isOperate || hasUrlShortLinkVal) {
      // 如果目前是继承模式，就设置继承值为过滤后的筛选器值
      // 其他页面不支持 lifetime/quarter 时间类型，继承时重置为 date，避免切换页面后筛选器显示"请选择"
      const rangeType = newFilterValues?.[filterKeyMap.RANGE_TIME]?.type;
      const extendValues = {
        ...newFilterValues,
        // Demographics 是单选数据源，继承给多选页面时需要补齐为包含所有数据源的数组
        [filterKeyMap.DATASOURCE]: {
          ...(newFilterValues?.[filterKeyMap.DATASOURCE] || {}),
          value: [SOURCE_LIST.SENSORTOWER, SOURCE_LIST.APPANNIE],
        },
        ...((rangeType === 'lifetime' || rangeType === 'quarter')
          ? {
              [filterKeyMap.RANGE_TIME]: {
                ...newFilterValues?.[filterKeyMap.RANGE_TIME],
                type: 'date',
              },
            }
          : {}),
      };
      setFilterExtend(extendValues);
    }
    setFilterVale(newFilterValues);

    // 初始化（含短链/缓存回填）时，同步 datasource + 国家 options 请求参数，保证 UI 与 options 立刻匹配
    setActiveDatasource?.(reqSource);
    setCountryFilterParams?.({ source: reqSource, granularity: reqGranularity });

    newFetchParams = {
      ...newFetchParams,
      ...(urlCustomParams || {}),
      ...apiParams,
      source: reqSource,
      granularity: reqGranularity,
    };
    getGameData({
      params: newFetchParams,
      isCheckGame: false,
      updateFilterValues: newFilterValues,
    });
  }, [userInfo?.userid, marketLoading, shortLinkFilterValue, JSON.stringify(urlCustomParams || {})]);

  // data.ai 切源：等国家下拉 options 刷新后，把国家默认值设置为下拉第一个（但不自动发榜单请求）
  useEffect(() => {
    if (!pendingDatasourceFetch) return;
    if (marketLoading) return;

    const markets = Array.isArray(allMarkets) ? allMarkets : [];
    const firstMarket = markets?.[0];
    if (!firstMarket) {
      return;
    }

    setFilterVale({
      ...(filterValue || {}),
      [filterKeyMap.COMPOSED_COUNTRY_SELECT]: {
        ...(filterValue?.[filterKeyMap.COMPOSED_COUNTRY_SELECT] || {}),
        value: {
          type: 'single',
          countries: [firstMarket],
        },
      },
    });

    // 不在切源时请求榜单数据：等用户点击"确认"后再请求
    setPendingDatasourceFetch(null);
  }, [pendingDatasourceFetch, marketLoading, JSON.stringify(allMarkets || []), JSON.stringify(filterValue || {})]);

  const getGameData = async (argms: {
    params: any;
    isCheckGame: boolean;
    isSearch?: boolean;
    updateFilterValues?: any;
    myFilterValue?: string;
  }) => {
    const {
      params: fetchParams,
      isCheckGame: isCheckOrSearch,
      isSearch = false,
      updateFilterValues = filterValue,
      myFilterValue = filterSaveName,
    } = argms;
    const params = {
      ...fetchParams,
      page_size: isSearch
        ? tableTotal || fetchParams.page_size // 参数不能为0
        : fetchParams.page_size,
      page: isSearch ? 1 : fetchParams.page,
      search_game_code: isSearch ? fetchParams.search_game_code : {},
    };
    // 当时间为空时，等时间返回再更新，否则直接写token到接口
    // 该页接口回包以 req.start_date/end_date 为准（并非总是带 date）
    const isUpdateShortLinkNow = !!(params?.start_date && params?.end_date);
    if (isUpdateShortLinkNow && filterCfg?.hasUrlShortLink) {
      updateShortLinkFilterId({ [URL_FILTER_KEY]: updateFilterValues });
    }
    try {
      const { granularity } = params;
      if(granularity === "quarterly"){
        params.end_date = params.start_date;
      }
      // lifetime 粒度 loading 期间先清空筛选器时间展示，等后端返回后再回填
      if (granularity === 'lifetime' && updateFilterValues) {
        const clearedFilterValue = updateTimeFilterValue({
          filterValue: updateFilterValues,
          timeFilterValues: {
            type: 'lifetime',
            value: ['', ''],
          },
        });
        setFilterVale(clearedFilterValue);
      }
      setTableData([]);
      setFetchParams(params);
      const res = await getGameBasicinfoNew(params);
      const { total, rank_list = [], age_sort } = res?.data || {};
      // let checkGameList = tableSelection;

      const tableList = handleTableDataFormat(rank_list, params);
      if (params.page === 1) {
        // 过滤rank==0后的游戏可勾选展示可视化
        const filterNoRankGameList = filterGamesWithRank(tableList);
        /** 第一页时拿到默认前6个游戏作为游戏全选值 */
        const defaultGames = handleTableDefaultSelectGames(
          filterNoRankGameList.slice(0, TOPGAMENUM),
          APP_ID,
        );
        setDefaultSelectionGames(defaultGames);
        /** 在第一页且没有勾选游戏时默认选中前6（游戏在排序或重新筛选时有更新） */
        if (!isCheckOrSearch) {
          setTableSelection(defaultGames);
          /** 分页排序请求接口时，可视化不改变 */
          const barOrPieDataTmp = filterNoRankGameList?.slice(0, TOPGAMENUM);
          setBarOrPieData(barOrPieDataTmp);

          // checkGameList = defaultGames;
        }
      }
      if (!isSearch /* !params?.game_code?.length*/) {
        // 有game_code表示是搜索，则不存页面数据
        setTableListForClearSearch(tableList || []);
        setTotalForClearSearch(total || tableList?.length);
        setAgeSortList(getAgeSortList(age_sort, ageBreakdownList));
        pageSizeBeforeSearchRef.current = params.page_size || 50;
      }
      const newFetchParams = {
        ...params,
        // 接口可能仍会返回 date（历史字段），但该页以 start/end 为准进行筛选器回填
        // date: res?.req?.date || params?.date || '',
        start_date: res?.req?.start_date || params?.start_date || '',
        end_date: res?.req?.end_date || params?.end_date || '',
        filter_ids: isSearch ? params?.filter_ids : res?.req?.filter_ids,
      };

      // 当 granularity 为 lifetime 时，记录后端返回的时间（供下载弹窗在 quarter↔lifetime 切换时使用）
      if (newFetchParams.granularity === 'lifetime' || newFetchParams.granularity === SOURCE_LIST.LIFETIME) {
        const s = newFetchParams.start_date;
        const e = newFetchParams.end_date;
        if (s && e) {
          lifetimeDateRef.current = { start: s, end: e };
        }
      }

      // 缓存接口返回的各数据源/时间类型的"最后有数据时间"，切换数据源或时间筛选器时用于回填空时间
      const reqData = res?.req || {};
      if (reqData.lifetime_latest_start_date) latestDatesRef.current.lifetime_latest_start_date = reqData.lifetime_latest_start_date;
      if (reqData.lifetime_latest_end_date) latestDatesRef.current.lifetime_latest_end_date = reqData.lifetime_latest_end_date;
      if (reqData.data_ai_latest_start_date) latestDatesRef.current.data_ai_latest_start_date = reqData.data_ai_latest_start_date;
      if (reqData.data_ai_latest_end_date) latestDatesRef.current.data_ai_latest_end_date = reqData.data_ai_latest_end_date;
      if (reqData.quarter_latest_date) latestDatesRef.current.quarter_latest_date = reqData.quarter_latest_date;

      setFetchParams(newFetchParams);
      setTableTotal(total || tableList?.length);
      setTableData(tableList || []);

      // 获取到时间后更新筛选器的值，并更新url短链接
      // - 该接口回包会修正 start/end（例如补齐默认时间、对齐可用时间范围）
      // - 不管请求时是否已带时间，只要回包携带 start/end，就回填到页面筛选器上
      const respStartDate = res?.req?.start_date;
      const respEndDate = res?.req?.end_date;
      const resGranularity = res?.req?.granularity;
      if (respStartDate && respEndDate) {
        const backendTimeType = resGranularity || updateFilterValues?.[filterKeyMap.RANGE_TIME]?.type || 'month';
        const newFilterValue = updateTimeFilterValue({
          haveFilterSaveBtn: true,
          filterSaveName: myFilterValue || '',
          filterValue: updateFilterValues,
          timeFilterValues: {
            type: backendTimeType,
            value: [respStartDate, respEndDate],
          },
        });
        setFilterVale(newFilterValue);
        // 其他页面不支持 lifetime/quarter 时间类型，继承时重置为 date，避免切换页面后筛选器显示"请选择"
        const extendTimeType = newFilterValue?.[filterKeyMap.RANGE_TIME]?.type;
        const extendFilterValue = {
          ...newFilterValue,
          // Demographics 是单选数据源，继承给多选页面时需要补齐为包含所有数据源的数组
          [filterKeyMap.DATASOURCE]: {
            ...(newFilterValue?.[filterKeyMap.DATASOURCE] || {}),
            value: [SOURCE_LIST.SENSORTOWER, SOURCE_LIST.APPANNIE],
          },
          ...((extendTimeType === 'lifetime' || extendTimeType === 'quarter')
            ? {
                [filterKeyMap.RANGE_TIME]: {
                  ...newFilterValue?.[filterKeyMap.RANGE_TIME],
                  type: 'date',
                },
              }
            : {}),
        };
        setFilterExtend(extendFilterValue);
        // 如果有短链接功能，则更新短链接
        if (!isUpdateShortLinkNow && filterCfg?.hasUrlShortLink) {
          // 这里会导致 shortLinkFilterValue 更新，从而触发上面的"初始化 useEffect"；标记跳过一次即可
          skipNextInitFetchRef.current = true;
          updateShortLinkFilterId({ [URL_FILTER_KEY]: newFilterValue });
        }
      }
    } catch (e) {
      console.error(e);
      setTableData([]);
      setBarOrPieData([]);
      setTableSelection([]);
    } finally {
      setTableLoading(false);
      setIsVisualisLoading(false);
    }
  };

  const onTableSortChage = (e: SortByItemType) => {
    const newFetchParams = {
      ...fetchParams,
      order: e?.sortVal,
      order_metric: e?.sortMetric,
      order_type: e?.orderType,
      ratio_type: e?.ratioType ?? 0,
      page: 1,
      // page_size: 100,
      page_size: 50,
    };
    setSortKeyName(e.title);
    setTableLoading(true);
    if (!operatedGame) {
      /** 没有勾选时点排序则重新Loading柱状图数据 */
      setIsVisualisLoading(true);
    }
    if (newFetchParams?.order_type && SEGMENT_KEYS.includes(newFetchParams.order_type)) {
      setSegVal(newFetchParams.order_type);
    }
    getGameData({ params: newFetchParams, isCheckGame: operatedGame });
  };

  const onTablePagingChange = (e: PagingQuery) => {
    const newFetchParams = {
      ...fetchParams,
      page: e?.pageIndex || 1,
      // page_size: e?.pageSize || 100,
      page_size: e?.pageSize || 50,
    };
    setTableLoading(true);
    getGameData({ params: newFetchParams, isCheckGame: operatedGame });
  };

  // 表格列表项选择事件回调
  const onTableCheckGame = (
    selectList: TableSelectionType[],
    checkStatus: TableSelectStatusType,
  ) => {
    // setIsNewComfirm(true);
    // setIsNewCheckGame(true);

    /** 是否勾选了游戏 */
    if (checkStatus === 'cancel' || checkStatus === 'all' || selectList.length === 0) {
      setOperatedGame(false);
    } else {
      setOperatedGame(true);
    }
    setBarOrPieData(selectList);
    setTableSelection(selectList);
  };

  /**
   * App Magic 品类标签点击回调
   * 点击标签后将该品类设置到筛选器中并重新请求数据
   */
  const handleAppMagicTagClick = (
    type: AppMagicGenreTagType,
    superTag: string,
    majorTag?: string,
    midTag?: string,
    subTag?: string,
  ) => {
    const appMagicOptions = filterCfg?.specialParams?.genreOptions?.app_magic;
    const genreOptionsLoading = filterCfg?.specialParams?.genreOptionsLoading;
    if (genreOptionsLoading || !appMagicOptions || appMagicOptions.length === 0) {
      return;
    }

    const newGenreValue = generateGenreSelectValueSimple(
      genreOptionsMap,
      'appmagic',
      superTag,
      majorTag,
      midTag,
      subTag,
    );
    if (!newGenreValue) {
      return;
    }

    filterRef.current?.setValueAndConfirm({
      [filterKeyMap.GENRETOR]: newGenreValue,
    });
  };

  const handleTagClick = buildTopChartsTagClickHandler({
    filterRef,
    filterValue,
    setFilterValue: setFilterVale,
    sensorTowerTagConfig: filterCfg?.specialParams?.sensorTowerTagConfig,
  });

  const handleConfirm = async (value: any, filter: any, myFilterValue: any) => {
    // 标记用户主动确认，避免后续"初始化请求"useEffect 因为 loading/短链变化再触发一次请求
    lastUserConfirmAtRef.current = Date.now();

    const reqSource = resolveReqSource(value, filter);
    const newFormValue = {
      ...fetchParams,
      ...value,
      source: reqSource,
      granularity: resolveReqGranularity(reqSource, filter),
      page: 1,
      // page_size: 100,
      page_size: 50,
      platforms: value.platforms, // databrain此参数默认为['unified'],
    };
    // 请求接口
    setTableLoading(true);
    setIsVisualisLoading(true);
    /** 修改筛选器后，游戏应该重置为未勾选状态 */
    setOperatedGame(false);
    await getGameData({
      params: newFormValue,
      isCheckGame: false,
      updateFilterValues: filter,
      myFilterValue,
    });
    // setIsNewComfirm(true);
    // setIsNewCheckGame(true);
  };

  const handleClearSearch = () => {
      setFetchParams({
        ...fetchParams,
        search_game_code: {},
        rank_ids: [],
        page: 1,
      page_size: pageSizeBeforeSearchRef.current,
    });
    setTableData(tableListForClearSearch);
    setTableTotal(totalForClearSearch);
  };

  const getTableSearchList = (id: string) => {
    setTableLoading(true);

    if (typeof id === 'string') {
      const [uid, appid] = id.split('game_id');
      getGameData({
        params: {
          ...(fetchParams || {}),
          search_game_code: uid && appid ? { [uid]: appid } : {},
          rank_ids: uid ? [uid] : [],
        },
        isCheckGame: true,
        isSearch: true,
      });
    }
  };

  const transTableDataForSearch = (res: any) => {
    const resultList = res?.data?.search_list;
    if (!Array.isArray(resultList) || !resultList?.length) {
      return [];
    }
    const tranksList: RecordItemType[] = resultList.map((item) => ({
      id: `${item?.unified_id}game_id${item?.game_id}`, // uid + appid
      imgSrc: item?.cover,
      lineOneStr: item?.game_name,
      lineTwoImg: getCountryFlag(item?.country_abbre),
      lineTwoImgName: item?.country_abbre,
      // imageType: '',
      lineTwoStr: item?.publisher,
      rank: item?.rank ?? item?.game_rank,
    }));

    return tranksList;
  };

  return (
    <>
      <MarqueeBanner />
      <Card>
        <Card.Body>
          <Filter
            ref={filterRef}
            filterConfirm={handleConfirm}
            filterReset={(newFormValue: any) => {
              // 重置时：回填已缓存的时间，避免重置后时间为空
              const rangeTimeType = newFormValue?.[filterKeyMap.RANGE_TIME]?.type;
              // lifetime：优先用 lifetimeDateRef，兜底用 latestDatesRef
              const ltStart = lifetimeDateRef.current.start || latestDatesRef.current.lifetime_latest_start_date;
              const ltEnd = lifetimeDateRef.current.end || latestDatesRef.current.lifetime_latest_end_date;
              if (rangeTimeType === 'lifetime' && ltStart && ltEnd) {
                const patched = {
                  ...newFormValue,
                  [filterKeyMap.RANGE_TIME]: {
                    type: 'lifetime',
                    value: [ltStart, ltEnd],
                  },
                };
                setFilterVale(patched);
                filterRef.current?.setFormValue({
                  [filterKeyMap.RANGE_TIME]: {
                    type: 'lifetime',
                    value: [ltStart, ltEnd],
                  },
                });
              }
            }}
            filterValue={filterValue}
            setFilterVale={setFilterVale}
            defaultValue={DEFAULT_FORM_VALUE}
            filterCfg={filterCfgWithHotTag}
            extendPageId={pageId}
            rangeTimeComponentDefault={rangeTimeComponentDefault}
            adjustFormValue={adjustFormValue}
            buriedPointParams={pageLogInfo}
          />
        </Card.Body>
      </Card>

      <Visualis
        segVal={segVal}
        setSegVal={setSegVal}
        orderMetric={fetchParams?.order_metric}
        source={fetchParams?.source}
        loading={isVisualisLoading}
        barOrPieData={barOrPieData}
        // fetchParams={fetchParams}
        // chartCardTitle={'Demographics Breakdown'}
        // chartsTitle={getDemographicChartSortName(apiParams?.order_metric, apiParams?.ratio_type)}
        // loading={loading || echartLoading || echartInsideLoading}
        cardStatus={{
          isLoading: isVisualisLoading,
          isError: false,
          isNoData: !barOrPieData?.length,
        }}
        dateType={'monthly'}
        sortKeyName={sortKeyName}
        orderKey={fetchParams?.order}
      />
      <Justify
        className="dbi-card-justify"
        right={
          <>
            <SearchRecent
              // 传入搜索的filter_ids要为[],不然后端会返回filter_ids里的游戏列表
              fetchParams={{ ...fetchParams, page: 1, filter_ids: [], rank_ids: [] }}
              handleClear={handleClearSearch}
              getTableResultList={getTableSearchList}
              searchListApi={getGameBasicinfoNew}
              pageLogInfo={pageLogInfo}
              searchType={'mobile-demographics'}
              initSearchInput={!(fetchParams?.rank_ids?.length > 0)}
              transTableDataForSearch={transTableDataForSearch}
            />
            <ExportTable
              downloadModalSize={820}
              entityType="mobile"
              businessType={'mobile-demographics'}
              isDisableDownload={tableLoading || marketLoading}
              total={tableTotal}
              // 暂时没做联动下载，接口也没做，先注释调select_ids字段入参
              // tableParams={{ ...fetchParams, select_ids: tableSelection }}
              tableParams={{ ...fetchParams, date_type: 'monthly', _lifetimeStartDate: lifetimeDateRef.current.start, _lifetimeEndDate: lifetimeDateRef.current.end, _quarterLatestDate: latestDatesRef.current.quarter_latest_date }}
              // isNeedCheckedLines={true}   //勾选为appid暂时不做此勾选联动功能
              isGameTopCharts={true}
              regionOptions={filterCfg?.specialParams?.regionOptions || []}
              downloadLogConfigKey={'TopChartsMobileGamesDetailDemographicsDownloadButton'}
              newStyleFileName={(() => {
                const base = 'Top Charts_Mobile Games_Demographics';
                if (!selectedHotTagOption) return base;
                const hotTagName = locale === 'zh-CN'
                  ? (selectedHotTagOption.name_cn || selectedHotTagOption.name || '')
                  : (selectedHotTagOption.name_en || selectedHotTagOption.name || '');
                return hotTagName ? `Hot Tags_${hotTagName}_${base}` : base;
              })()}
            />
          </>
        }
      />
      <DemographicsTable
        entityType={entityType}
        fetchParams={fetchParams}
        // showFirstGuide={showFirstGuide}
        records={tableData}
        loading={tableLoading}
        tableTotal={tableTotal}
        tableSelection={tableSelection}
        tableSelectionAll={defaultSelectionGames}
        pageSortAttr={{
          tablePageIndex: fetchParams?.page,
          tablePageSize: fetchParams?.page_size,
          currentSortValue: fetchParams?.order,
          currentSortMetric: fetchParams?.order_metric,
          currentOrderSource: fetchParams?.order_type,
          currentRatioType: fetchParams?.ratio_type,
          // currentSortMetric: `${fetchParams?.order_metric}_${fetchParams?.order_source}`,
        }}
        onTableSortChage={onTableSortChage}
        onTablePagingChange={onTablePagingChange}
        onTableCheckGame={onTableCheckGame}
        // selectDataSources={fetchParams?.data_sources}
        // topChartsType={topChartsType}
        // dateType={fetchParams?.date_type}
        genreSource={fetchParams?.genre_source ?? 'sensortower'}
        ageSortList={ageSortList}
        onAppMagicTagClick={handleAppMagicTagClick}
        filterValue={filterValue}
        gameDetailsLoading={gameDetailsLoading}
        gameDetailsMapping={gameDetailsMapping}
        onTagClick={handleTagClick}
        source={fetchParams?.source || 'sensortower'}
        pageId={pageId}
      />
    </>
  );
}
